import { t } from '@/i18n/i18ns'
import _ from 'lodash'
import { PLAYER_ARRANGEMENT_OPTIONS, PLAYER_ARRANGEMENTS } from '../../player-setup-arrangement/player-setup-arrangement'
import { convertStandardToObjectResolution } from '../../player-data-builder'
import PlayerSetupVideowallUnit from './player-setup-videowall-unit/player-setup-videowall-unit-tmpl.vue'
import { asyncValidationRule } from '@/element-ui/form-utils'
import PlayerSetupCanvas from '../../player-setup-canvas/player-setup-canvas-tmpl'
import { PageResponse } from '../../player-setup-communication'
import PlayerSetupFormError from '../../player-setup-form-error/player-setup-form-error-tmpl'
import { assignDisplayPreviews } from '../player-setup-adjustments-preview'

export const validateScreenDiagonal = asyncValidationRule(async (rule, value) => {
  if (!value) {
    return new Error(t('playerSetup.requiredScreenDiagonal'))
  } else if (_.isNaN(_.toNumber(value)) || _.toNumber(value) < 1) {
    return new Error(t('playerSetup.incorrectInput'))
  }
})

export const validateHorizontalBezel = asyncValidationRule(async (rule, value) => {
  if (!value) {
    return new Error(t('playerSetup.requiredHorizontalBezel'))
  } else if (_.isNaN(_.toNumber(value)) || _.toNumber(value) < 1) {
    return new Error(t('playerSetup.incorrectInput'))
  }
})

export const validateVerticalBezel = asyncValidationRule(async (rule, value) => {
  if (!value) {
    return new Error(t('playerSetup.requiredVerticalBezel'))
  } else if (_.isNaN(_.toNumber(value)) || _.toNumber(value) < 1) {
    return new Error(t('playerSetup.incorrectInput'))
  }
})

export const applyConversion = (screenDiagonalUnit, bezelUnit, horizontalBezel, verticalBezel) => {
  const convert = (to, value) => {
    const IN_TO_MM = 25.4
    const MM_TO_IN = 0.0393701

    return to === 'mm' ? value * IN_TO_MM : value * MM_TO_IN
  }

  let convertedHorizontalBezel = horizontalBezel
  let convertedVerticalBezel = verticalBezel

  if (screenDiagonalUnit !== bezelUnit) {
    convertedHorizontalBezel = convert(screenDiagonalUnit, horizontalBezel)
    convertedVerticalBezel = convert(screenDiagonalUnit, verticalBezel)
  }

  return {
    convertedVerticalBezel,
    convertedHorizontalBezel
  }
}

export default {
  name: 'player-setup-videowall',
  components: {
    PlayerSetupVideowallUnit,
    PlayerSetupCanvas,
    PlayerSetupFormError
  },
  props: {
    isActive: {
      type: Boolean,
      default: false
    },
    nextPage: {
      type: [String, Object, null],
      default: null
    },
    arrangementName: {
      type: [String, null],
      default: () => ''
    },
    displayOrientation: {
      type: String,
      default: 'horizontal'
    },
    applicationType: {
      type: [String, null],
      default: null
    },
    selectedResolution: {
      type: [String, null],
      default: null
    },
    canvasSize: {
      type: String,
      default: ''
    },
    externalError: {
      type: [String, null],
      default: null
    }
  },
  data () {
    return {
      model: {
        screenDiagonal: '55',
        screenDiagonalUnit: 'in',
        horizontalBezel: '1',
        verticalBezel: '1',
        bezelUnit: 'mm'
      },
      modelRules: {
        screenDiagonal: [
          { validator: validateScreenDiagonal, trigger: 'change' }
        ],
        horizontalBezel: [
          { validator: validateHorizontalBezel, trigger: 'change' }
        ],
        verticalBezel: [
          { validator: validateVerticalBezel, trigger: 'change' }
        ]
      },
      isVerticalBezelFocus: false,
      isHorizontalBezelFocus: false,
      showCanvasNameInput: false,
      resolution: {
        width: 1920,
        height: 1080
      },
      arrangement: null,
      languageCode: navigator?.language ?? 'en',
      canvasName: '',
      canvasTag: null,
      canvasCheckInProgress: false
    }
  },
  watch: {
    isActive: {
      immediate: true,
      handler (newValue, oldValue) {
        if (newValue !== oldValue && newValue) {
          this.setModelData()
        }
      }
    },
    arrangementName: {
      immediate: true,
      handler (newValue, oldValue) {
        if (newValue && newValue !== oldValue) {
          this.arrangement = PLAYER_ARRANGEMENT_OPTIONS[newValue]
        }
      }
    },
    selectedResolution: {
      immediate: true,
      handler (newValue) {
        if (newValue) {
          this.resolution = convertStandardToObjectResolution(newValue)
        }
      }
    }
  },
  computed: {
    tableData () {
      let data = []
      if (this.arrangement) {
        const selectedArrangement = this.arrangement.arrangement ?? PLAYER_ARRANGEMENTS.x2x2
        for (let i = 1; i <= this.arrangement.outputs; i++) {
          data.push({
            label: `HDMI ${i}`,
            width: this.resolution?.width ?? 1920,
            height: this.resolution?.height ?? 1080
          })
        }

        data = assignDisplayPreviews(data, this.displayOrientation, selectedArrangement)
      }

      return data
    },
    maxWidth () {
      if (this.resolution?.width) {
        return new Intl.NumberFormat(this.languageCode)
          .format(this.resolution?.width ?? 1920)
      }

      return ''
    },
    maxHeight () {
      if (this.resolution?.height) {
        return new Intl.NumberFormat(this.languageCode)
          .format(this.resolution?.height ?? 1080)
      }

      return ''
    },
    outputBoxProperty () {
      return {
        'h-[160px] w-[90px]': /^vertial_horizontal_x[2-4]$/g.test(this.arrangementName) || this.isArrangementNameVertical2x2(),
        'h-[90px] w-[160px]': /^horizontal_(vertical|horizontal)_x[2-4]$/g.test(this.arrangementName) || this.isArrangementNameHorizontal2x2(),
        'h-[96px] w-[72px]': /^vertial_vertical_x[2-4]$/g.test(this.arrangementName),
        'higlight-vertical': this.isVerticalBezelFocus && (!this.isArrangementNameHorizontal2x2() && !this.isArrangementNameVertical2x2()),
        'higlight-horizontal': this.isHorizontalBezelFocus && (!this.isArrangementNameHorizontal2x2() && !this.isArrangementNameVertical2x2()),
        'higlight-vertical-2x2': this.isVerticalBezelFocus && (this.isArrangementNameHorizontal2x2() || this.isArrangementNameVertical2x2()),
        'higlight-horizontal-2x2': this.isHorizontalBezelFocus && (this.isArrangementNameHorizontal2x2() || this.isArrangementNameVertical2x2())
      }
    },
    gridProperty () {
      return {
        'grid-cols-1': /^(vertial|horizontal)_vertical_x[2-4]$/g.test(this.arrangementName),
        'grid-cols-2': /^(vertial|horizontal)_horizontal_x2$/g.test(this.arrangementName) || (this.isArrangementNameHorizontal2x2() || this.isArrangementNameVertical2x2()),
        'grid-cols-3': /^(vertial|horizontal)_horizontal_x3$/g.test(this.arrangementName),
        'grid-cols-4': /^(vertial|horizontal)_horizontal_x4$/g.test(this.arrangementName)
      }
    },
    gridWrapper () {
      if (/^horizontal_horizontal_x[2-4]$/g.test(this.arrangementName)) {
        return 'absolute mt-[100px] w-full ml-28'
      } else if (/^horizontal_vertical_x[2-3]$/g.test(this.arrangementName) || /^vertial_horizontal_x[2-4]$/g.test(this.arrangementName)) {
        return 'w-[54%] mt-8'
      } else {
        return 'w-[54%]'
      }
    },
    showHorizontalBezel () {
      return /^(vertial|horizontal)_vertical_x[2-4]$/g.test(this.arrangementName) === true || (this.isArrangementNameHorizontal2x2() || this.isArrangementNameVertical2x2())
    },
    showVerticalBezel () {
      return /^(vertial|horizontal)_vertical_x[2-4]$/g.test(this.arrangementName) === false || (this.isArrangementNameHorizontal2x2() || this.isArrangementNameVertical2x2())
    },
    canvasWidth () {
      return this.canvasSize ? Number(this.canvasSize.split(' ')[0]) : 0
    },
    canvasHeight () {
      return this.canvasSize ? Number(this.canvasSize.split(' ')[1]) : 0
    }
  },
  methods: {
    isArrangementNameHorizontal2x2 () {
      return this.arrangementName === 'horizontal_x2x2'
    },
    isArrangementNameVertical2x2 () {
      return this.arrangementName === 'vertial_x2x2'
    },
    async onModelChange (value, modelName) {
      if (modelName) {
        this.model[modelName] = value
      }
      if (value && this.isActive) {
        await this.triggerAdjustmentRecalculation()
      }
    },
    async triggerAdjustmentRecalculation () {
      await this.$emit('applyAdjustments')
    },
    saveCanvasName (canvasName) {
      this.model.canvasName = canvasName
    },
    async setModelData () {
      this.arrangement = PLAYER_ARRANGEMENT_OPTIONS[this.arrangementName]
      await this.triggerAdjustmentRecalculation()
      this.$refs.screenDiagonal.focus()
    },
    async submitForm () {
      if (this.canvasCheckInProgress) {
        return PageResponse(true)
      }

      const { horizontalBezel, verticalBezel, screenDiagonal, bezelUnit, screenDiagonalUnit } = this.model
      const { convertedHorizontalBezel, convertedVerticalBezel } = applyConversion(screenDiagonalUnit, bezelUnit, horizontalBezel, verticalBezel)

      return PageResponse(false, this.nextPage, {
        data: {
          bezels: {
            horizontalBezel: convertedHorizontalBezel,
            verticalBezel: convertedVerticalBezel,
            screenDiagonal
          },
          canvasName: this.canvasName,
          canvasTag: this.canvasTag
        }
      })
    },
    async triggerFormSubmit (event) {
      this.$emit('SubmitForm')
      return false
    },
    updateCanvasInfo (name, tag) {
      this.canvasName = name
      this.canvasTag = tag
    },
    async toggleCanvasCheckProgress (isInProgress) {
      this.canvasCheckInProgress = isInProgress
      await await this.$nextTick()
    }
  }
}
